<script setup lang="ts">
	import { useQueryClient } from '@tanstack/vue-query'
	import { ref, watch, computed } from 'vue'
	import { useToast } from 'vue-toastification'

	import ImageCropper from '@/components/ImageCropper.vue'
	import LoadingCircle from '@/components/icons/LoadingCircle.vue'

	import { useAuth } from '@/composables/useAuth'

	import type { User } from '@/modules/users/interfaces/user'

	import useUserMedia from '../../composables/useUserMedia'

	interface Props {
		user: User
	}

	interface Emits {
		(e: 'refetch'): void
	}

	const props = defineProps<Props>()
	const emits = defineEmits<Emits>()
	const { userMediaCreateAsync } = useUserMedia({ immediate: false })
	const { user: userLogged } = useAuth()
	const toast = useToast()
	const ALLOWED_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/gif']
	const MAX_FILE_SIZE = 20 * 1024 * 1024
	const logoFile = ref<File | null>(null)
	const updatingLogo = ref(false)
	const addingLogo = ref(false)
	const dragOver = ref(false)
	const imagePreview = ref<string | null>(null)
	const showCropperModal = ref(false)

	// Computed para determinar si hay avatar actual
	const hasCurrentAvatar = computed(() => {
		return props.user?.avatar && props.user.avatar.length > 0
	})

	const isValidImageFile = (file: File): boolean => {
		if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
			toast.error('Formato no permitido. Usa PNG, JPG, JPEG o GIF.')
			return false
		}
		if (file.size > MAX_FILE_SIZE) {
			toast.error('La imagen supera el límite de 20 MB.')
			return false
		}
		return true
	}

	// Función para generar vista previa de la imagen y abrir el cropper
	const generateImagePreview = (file: File) => {
		const reader = new FileReader()
		reader.onload = (e) => {
			imagePreview.value = e.target?.result as string
			showCropperModal.value = true
		}
		reader.readAsDataURL(file)
	}

	const query = useQueryClient()

	const handleLogoSubmit = async () => {
		updatingLogo.value = true
		if (logoFile.value) {
			try {
				await userMediaCreateAsync({
					params: {
						userId: props.user?.id,
						media: logoFile.value,
						collection: 'avatar',
						expirationDate: null,
					},
					config: {
						onSuccessToast: 'Foto de perfil actualizada exitosamente',
						raiseToast: true,
					},
				})

				emits('refetch')

				addingLogo.value = false
				imagePreview.value = null
				logoFile.value = null
			} catch (err) {
				console.log(err)
			} finally {
				updatingLogo.value = false
			}
		}
	}

	const onCropApplied = async (file: File) => {
		logoFile.value = file
		showCropperModal.value = false
		await handleLogoSubmit()
	}

	const onCropCancelled = () => {
		showCropperModal.value = false
	}

	// Manejar drag and drop
	const handleDragOver = (e: DragEvent) => {
		e.preventDefault()
		dragOver.value = true
	}

	const handleDragLeave = (e: DragEvent) => {
		e.preventDefault()
		dragOver.value = false
	}

	const handleDrop = (e: DragEvent) => {
		e.preventDefault()
		dragOver.value = false
		const files = e.dataTransfer?.files
		if (files && files.length > 0) {
			const file = files[0]
			if (isValidImageFile(file)) {
				logoFile.value = file
				generateImagePreview(file)
			}
		}
	}

	// Manejar selección de archivo
	const handleFileSelect = (event: Event) => {
		const target = event.target as HTMLInputElement
		const file = target.files?.[0]
		if (file && isValidImageFile(file)) {
			logoFile.value = file
			generateImagePreview(file)
		}
	}

	const cancelEditing = () => {
		addingLogo.value = false
		logoFile.value = null
		imagePreview.value = null
		showCropperModal.value = false
	}

	watch(addingLogo, (value) => {
		if (!value) {
			logoFile.value = null
			imagePreview.value = null
			showCropperModal.value = false
		}
	})
</script>

<template>
	<div
		class="flex flex-col items-center space-y-6 p-6 bg-white rounded-xl shadow-sm border border-gray-100"
	>
		<!-- Header -->
		<div class="text-center">
			<h3 class="text-lg font-semibold text-gray-800 mb-1">Foto de Perfil</h3>
			<p class="text-sm text-gray-600">
				{{
					addingLogo ?
						'Selecciona una nueva imagen'
					:	'Gestiona tu foto de perfil'
				}}
			</p>
		</div>

		<!-- Avatar Display / Preview Area -->
		<div class="relative">
			<!-- Current Avatar or Placeholder -->
			<div
				v-if="!addingLogo"
				class="relative group transition-all duration-300"
			>
				<div
					class="w-32 h-32 rounded-full overflow-hidden bg-gray-100 border-4 border-white shadow-lg"
				>
					<img
						v-if="hasCurrentAvatar"
						:src="user.avatar"
						:alt="`Foto de ${user.name}`"
						class="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
					/>
					<div
						v-else
						class="w-full h-full flex items-center justify-center bg-gradient-to-br from-blue-400 to-purple-500 text-white text-2xl font-bold"
					>
						{{ user.name?.charAt(0) }}{{ user.lastname?.charAt(0) }}
					</div>
				</div>

				<!-- Overlay on hover -->
				<div
					v-if="userLogged?.id === user?.id"
					class="absolute inset-0 bg-black bg-opacity-40 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 cursor-pointer"
					@click="addingLogo = true"
				>
					<svg
						class="w-8 h-8 text-white"
						fill="none"
						stroke="currentColor"
						viewBox="0 0 24 24"
					>
						<path
							stroke-linecap="round"
							stroke-linejoin="round"
							stroke-width="2"
							d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"
						></path>
						<path
							stroke-linecap="round"
							stroke-linejoin="round"
							stroke-width="2"
							d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"
						></path>
					</svg>
				</div>
			</div>

			<!-- Drag & Drop / Selection Area -->
			<div
				v-if="addingLogo"
				class="relative"
				@dragover="handleDragOver"
				@dragleave="handleDragLeave"
				@drop="handleDrop"
			>
				<div
					:class="[
						'border-2 border-dashed rounded-xl p-8 text-center transition-all duration-300',
						dragOver ?
							'border-blue-400 bg-blue-50'
						:	'border-gray-300 hover:border-blue-400 hover:bg-gray-50',
						logoFile ? 'border-green-400 bg-green-50' : '',
					]"
				>
					<input
						type="file"
						accept="image/*"
						@change="handleFileSelect"
						class="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
					/>

					<div class="space-y-4">
						<div
							class="mx-auto w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center"
						>
							<svg
								class="w-6 h-6 text-gray-600"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24"
							>
								<path
									stroke-linecap="round"
									stroke-linejoin="round"
									stroke-width="2"
									d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
								></path>
							</svg>
						</div>

						<div>
							<p class="text-sm font-medium text-gray-700">
								{{
									dragOver ? '¡Suelta la imagen aquí!'
									: logoFile ? logoFile.name
									: 'Arrastra una imagen o haz clic para seleccionar'
								}}
							</p>
							<p class="text-xs text-gray-500 mt-1">
								PNG, JPG, JPEG o GIF hasta 20 MB
							</p>
						</div>
					</div>
				</div>
			</div>
		</div>

		<!-- Action Buttons -->
		<div class="flex flex-col sm:flex-row gap-3 w-full max-w-sm">
			<!-- Edit Mode Controls -->
			<template v-if="addingLogo">
				<button
					type="button"
					@click="cancelEditing"
					class="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200 transition-colors duration-200"
				>
					Cancelar
				</button>
			</template>

			<!-- Default Controls -->
			<template v-else>
				<button
					v-if="userLogged?.id === user?.id"
					@click="addingLogo = true"
					class="flex-1 px-6 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-all duration-200 flex items-center justify-center gap-2 shadow-md hover:shadow-lg transform hover:scale-105"
				>
					<svg
						class="w-4 h-4"
						fill="none"
						stroke="currentColor"
						viewBox="0 0 24 24"
					>
						<path
							stroke-linecap="round"
							stroke-linejoin="round"
							stroke-width="2"
							d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"
						></path>
						<path
							stroke-linecap="round"
							stroke-linejoin="round"
							stroke-width="2"
							d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"
						></path>
					</svg>
					{{ hasCurrentAvatar ? 'Cambiar Foto' : 'Agregar Foto' }}
				</button>
			</template>
		</div>

		<!-- Status Messages -->
		<div
			v-if="updatingLogo"
			class="text-center"
		>
			<div class="flex items-center justify-center gap-2 text-blue-600">
				<LoadingCircle class="!h-5 !w-5" />
				<span class="text-sm font-medium">Actualizando foto de perfil...</span>
			</div>
		</div>

		<!-- Crop Modal -->
		<ImageCropper
			v-model:isVisible="showCropperModal"
			:image-src="imagePreview || ''"
			stencil-shape="circle"
			:uploading="updatingLogo"
			@crop-applied="onCropApplied"
			@crop-cancelled="onCropCancelled"
		/>
	</div>
</template>

<style scoped>
	/* Animaciones y transiciones suaves */
	.transition-all {
		transition-property: all;
		transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
	}

	.duration-200 {
		transition-duration: 200ms;
	}

	.duration-300 {
		transition-duration: 300ms;
	}

	/* Efectos hover para botones */
	.transform:hover {
		transform: scale(1.05);
	}

	.hover\:scale-105:hover {
		transform: scale(1.05);
	}

	.hover\:scale-110:hover {
		transform: scale(1.1);
	}

	/* Shadows personalizadas */
	.shadow-sm {
		box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
	}

	.shadow-md {
		box-shadow:
			0 4px 6px -1px rgba(0, 0, 0, 0.1),
			0 2px 4px -1px rgba(0, 0, 0, 0.06);
	}

	.shadow-lg {
		box-shadow:
			0 10px 15px -3px rgba(0, 0, 0, 0.1),
			0 4px 6px -2px rgba(0, 0, 0, 0.05);
	}

	.hover\:shadow-lg:hover {
		box-shadow:
			0 10px 15px -3px rgba(0, 0, 0, 0.1),
			0 4px 6px -2px rgba(0, 0, 0, 0.05);
	}

	/* Efectos para el área de drag & drop */
	.border-dashed {
		border-style: dashed;
	}

	/* Gradientes */
	.bg-gradient-to-br {
		background-image: linear-gradient(
			to bottom right,
			var(--tw-gradient-stops)
		);
	}

	.from-blue-400 {
		--tw-gradient-from: #60a5fa;
		--tw-gradient-stops:
			var(--tw-gradient-from), var(--tw-gradient-to, rgba(96, 165, 250, 0));
	}

	.to-purple-500 {
		--tw-gradient-to: #8b5cf6;
	}

	/* Estados de hover específicos */
	.group:hover .group-hover\:opacity-100 {
		opacity: 1;
	}

	.group:hover .group-hover\:scale-110 {
		transform: scale(1.1);
	}

	/* Espaciado */
	.space-y-4 > :not([hidden]) ~ :not([hidden]) {
		margin-top: 1rem;
	}

	.space-y-6 > :not([hidden]) ~ :not([hidden]) {
		margin-top: 1.5rem;
	}

	/* Responsive design */
	@media (max-width: 640px) {
		.flex-col.sm\:flex-row {
			flex-direction: column;
		}

		.max-w-sm {
			max-width: 100%;
		}
	}

	/* Mejoras visuales para el input file */
	input[type='file'] {
		cursor: pointer;
	}

	input[type='file']::-webkit-file-upload-button {
		visibility: hidden;
	}

	input[type='file']::before {
		content: '';
		display: inline-block;
		background: transparent;
		border: none;
		cursor: pointer;
	}
</style>
